import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  createContext,
  useContext,
  useRef,
} from 'react';

import { Table, Button } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SearchBar from './SearchBar';

import request from '@/request';

const RowContext = createContext();

export const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext) || {};
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const SortableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

/** DataTable：展示带查询、分页以及操作区的数据表格 */
const DataTable = forwardRef(
  (
    {
      options = [],
      exportAPI,
      onImport,
      importAPI,
      onCreate,
      columns,
      list = [],
      pagination,
      rowKey = 'id',
      api = '',
      unit = '条',
      components,
      onSortSubmit,
      addType,
      demoDownloadUrl,
      showHeader = true,
      queryExtra,
      dataSourceFilter,
      onDataSourceChange,
    },
    ref
  ) => {
    const [page, setPage] = useState(pagination?.page || 1);
    const [pageSize, setPageSize] = useState(pagination?.pageSize || 10);
    const [total, setTotal] = useState(0);
    const [dataSource, setDataSource] = useState(list);
    const [dragData, setDragData] = useState(list);

    useEffect(() => {
      if (!api && JSON.stringify(list) !== JSON.stringify(dataSource)) {
        setDataSource(list);
      }
    }, [list, api, dataSource]);

    useEffect(() => {
      onDataSourceChange?.(dataSource);
    }, [dataSource, onDataSourceChange]);

    const filteredDataSource = useMemo(() => {
      if (!dataSourceFilter) return dataSource;
      return dataSource.filter(dataSourceFilter);
    }, [dataSource, dataSourceFilter]);

    // 搜索参数单独管理
    const searchParams = useRef('');

    const loadData = useCallback(
      async (params) => {
        if (params !== undefined && params !== searchParams.current) {
          searchParams.current = params;
          setPage(1);
          if (page !== 1) return;
        }
        if (!api) return;
        const res = await request.get(
          `${api}${api.indexOf('?') === -1 ? '?' : '&'}page=${page}&pageSize=${pageSize}&${searchParams.current}`
        );
        if (Array.isArray(res)) {
          setDataSource(res);
        } else if (Array.isArray(res.items)) {
          setDataSource(res.items);
          setTotal(res.total || 0);
        }
      },
      [api, page, pageSize]
    );

    useEffect(() => {
      if (options.length === 0 && api) loadData();
    }, [options.length, api, loadData]);

    // sync external list for drag view
    useEffect(() => {
      if (onSortSubmit) setDragData(list);
    }, [list, onSortSubmit]);

    // stable drag handler to prevent infinite updates
    const handleDragEnd = useCallback(
      async ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setDragData((prevData) => {
          const sourceIndex = prevData.findIndex(
            (i) => i[rowKey] === active.id
          );
          const overIndex = prevData.findIndex((i) => i[rowKey] === over.id);
          const newData = arrayMove(prevData, sourceIndex, overIndex);
          onSortSubmit?.(newData);
          return newData;
        });
      },
      [rowKey, onSortSubmit]
    );

    // 暴露刷新方法给父组件
    useImperativeHandle(ref, () => ({
      refresh: () => {
        setPage(1);
        loadData();
      },
    }));

    return (
      <div className="w-full space-y-6 min-w-0">
        {options.length > 0 && (
          <SearchBar
            options={options}
            exportAPI={exportAPI}
            importAPI={importAPI}
            onImport={onImport}
            onCreate={onCreate}
            onSearch={loadData}
            onReset={() => {
              setPage(1);
              loadData();
            }}
            addType={addType}
            demoDownloadUrl={demoDownloadUrl}
            queryExtra={queryExtra}
          />
        )}
        <div className="overflow-x-auto min-w-0" style={{ width: '100%' }}>
          {onSortSubmit ? (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(dragData.length ? dragData : dataSource).map(
                  (i) => i[rowKey]
                )}
                strategy={verticalListSortingStrategy}
              >
                <Table
                  columns={columns}
                  dataSource={dragData.length ? dragData : dataSource}
                  components={{
                    body: { row: SortableRow },
                    ...components?.body,
                  }}
                  pagination={false}
                  rowKey={rowKey}
                  scroll={{ x: 'max-content' }}
                  tableLayout="auto"
                  locale={{ emptyText: '暂无数据' }}
                  className="min-w-0"
                  showHeader={showHeader}
                />
              </SortableContext>
            </DndContext>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredDataSource}
              components={components}
              pagination={
                pagination === false
                  ? false
                  : {
                      current: page,
                      pageSize: pageSize,
                      total: total,
                      showSizeChanger: true,
                      hideOnSinglePage: true,
                      onChange: (newPage, newPageSize) => {
                        setPage(newPage);
                        setPageSize(newPageSize);
                      },
                      showTotal: (total) => `共${total}${unit}`,
                    }
              }
              rowKey={rowKey}
              scroll={{ x: 'max-content' }}
              tableLayout="auto"
              locale={{ emptyText: '暂无数据' }}
              className="min-w-0"
              showHeader={showHeader}
            />
          )}
        </div>
      </div>
    );
  }
);

export default DataTable;
